import {useFormatWithSettings} from '_logics/LFCFormatUtil';
import Decimal from 'decimal.js-light';
import React, {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';

import {CloudDistanceDigGraphBaseData, SearchConditions} from '../types';
import LFCChartsCloudDistanceMinDistribution from './LFCChartsCloudDistanceMinDistribution';
import LFCChartsCloudDistanceMinTrend from './LFCChartsCloudDistanceMinTrend';
import LFCChartsCloudDistanceMinVariationTrend from './LFCChartsCloudDistanceMinVariationTrend';

interface CloudDistanceMinAnalysisProps {
  searchConditions: SearchConditions;
  listData: any;
  graphBaseData: CloudDistanceDigGraphBaseData;
}

export const CloudDistanceMinAnalysis = ({
  searchConditions,
  listData,
  graphBaseData
}: CloudDistanceMinAnalysisProps) => {
  const {t} = useTranslation();
  const {fmtSizeByMm, fmtFloat} = useFormatWithSettings();
  const [zoomChartOpen, setZoomChartOpen] = useState<string>('');

  const handleZoomCharts = useCallback((chartId?: string) => {
    setZoomChartOpen(chartId || '');
  }, []);

  const handleZoomChartsClose = useCallback(() => {
    setZoomChartOpen('');
  }, []);

  const [maxUpperDistance, setMaxUpperDistance] = useState<number>(0);
  const [minUpperDistance, setMinUpperDistance] = useState<number>(0);
  const [averageUpperDistance, setAverageUpperDistance] = useState<number>(0);
  const [medianUpperDistance, setMedianUpperDistance] = useState<number>(0);
  const [standardDeviation, setStandardDeviation] = useState<number>(0);
  const [averagePlus3SD, setAveragePlus3SD] = useState<number>(0);
  const [averageMinus3SD, setAverageMinus3SD] = useState<number>(0);
  const [cp, setCp] = useState<number>(0);
  const [cpk, setCpk] = useState<number>(0);
  const [xRenge, setXRenge] = useState<number>(0);

  useEffect(() => {
    const newFilteredData = listData
      .map((b: any) => b.lower_distance)
      .flat()
      .filter((value: any) => value.length !== 0);

    if (newFilteredData.length === 0) {
      // データが空の場合は初期値を設定
      setMaxUpperDistance(0);
      setMinUpperDistance(0);
      setAverageUpperDistance(0);
      setMedianUpperDistance(0);
      setStandardDeviation(0);
      setAveragePlus3SD(0);
      setAverageMinus3SD(0);
      setCp(0);
      setCpk(0);
      setXRenge(0);
      return;
    }

    setMaxUpperDistance(Math.max(...newFilteredData));
    setMinUpperDistance(Math.min(...newFilteredData));

    // 平均値計算
    const sum = newFilteredData.reduce((acc: Decimal, value: number) => {
      return acc.plus(new Decimal(value));
    }, new Decimal(0));
    const newAverageUpperDistance = sum.dividedBy(newFilteredData.length).toNumber();
    setAverageUpperDistance(newAverageUpperDistance);

    const newMedianUpperDistance = (() => {
      const sorted = [...newFilteredData].sort((a, b) => a - b);
      const middle = Math.floor(sorted.length / 2);
      if (sorted.length % 2 !== 0) {
        return sorted[middle];
      } else {
        // 中央値計算
        const val1 = new Decimal(sorted[middle - 1]);
        const val2 = new Decimal(sorted[middle]);
        return val1.plus(val2).dividedBy(2).toNumber();
      }
    })();
    setMedianUpperDistance(newMedianUpperDistance);

    // 分散計算
    const avgDecimal = new Decimal(newAverageUpperDistance);
    const varianceSum = newFilteredData.reduce((acc: Decimal, value: number) => {
      const diff = new Decimal(value).minus(avgDecimal);
      return acc.plus(diff.pow(2));
    }, new Decimal(0));
    const newVariance = varianceSum.dividedBy(newFilteredData.length).toNumber();
    const newStandardDeviation = Math.sqrt(newVariance);
    setStandardDeviation(newStandardDeviation);

    // 3σ計算
    const stdDecimal = new Decimal(newStandardDeviation);
    const threeSigma = stdDecimal.times(3);
    setAveragePlus3SD(avgDecimal.plus(threeSigma).toNumber());
    setAverageMinus3SD(avgDecimal.minus(threeSigma).toNumber());

    const newUpperLimit = listData.filter((a: any) => a.map === searchConditions.map)[0]
      ?.upper_limit;
    const newLowerLimit = listData.filter((a: any) => a.map === searchConditions.map)[0]
      ?.lower_limit;

    // Cp計算
    let newCp = 0;
    if (newStandardDeviation !== 0 && newUpperLimit != null && newLowerLimit != null) {
      const upperLimitDecimal = new Decimal(newUpperLimit);
      const lowerLimitDecimal = new Decimal(newLowerLimit);
      const sixSigma = stdDecimal.times(6);
      newCp = upperLimitDecimal.minus(lowerLimitDecimal).dividedBy(sixSigma).toNumber();
    }
    setCp(newCp);

    // Cpk計算
    let newCpk = 0;
    if (newStandardDeviation !== 0 && newUpperLimit != null && newLowerLimit != null) {
      const upperLimitDecimal = new Decimal(newUpperLimit);
      const lowerLimitDecimal = new Decimal(newLowerLimit);
      const val1 = upperLimitDecimal.minus(avgDecimal).abs().dividedBy(threeSigma).toNumber();
      const val2 = avgDecimal.minus(lowerLimitDecimal).abs().dividedBy(threeSigma).toNumber();
      newCpk = Math.min(val1, val2);
    }
    setCpk(newCpk);

    // xRenge計算
    let newXRenge = 0;
    if (newUpperLimit != null && newLowerLimit != null) {
      const tmp_n: number = newFilteredData.length;
      let tmp_k: number;
      const logValue = Math.floor(Math.log2(tmp_n) + 1);
      if (logValue <= 7) {
        tmp_k = 7;
      } else if (logValue >= 27) {
        tmp_k = 27;
      } else {
        tmp_k = logValue;
      }

      const upperLimitDecimal = new Decimal(newUpperLimit);
      const lowerLimitDecimal = new Decimal(newLowerLimit);
      const tmp_h = upperLimitDecimal.minus(lowerLimitDecimal).dividedBy(tmp_k);
      newXRenge = tmp_h.toNumber();
    }
    setXRenge(newXRenge);
  }, [graphBaseData]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography sx={{fontWeight: 'bold'}}>{t('最小値分析')}</Typography>
      </Grid>
      <Grid item xs={12} md={6}>
        <LFCChartsCloudDistanceMinTrend
          title={t('距離推移')}
          source={graphBaseData}
          x={{dsColumn: '', type: 'category'}}
          y={{dsColumn: '', type: 'value'}}
          exportFilename={`CloudDistance_${t('最小値距離推移')}`}
          height={'20vh'}
          zoomChartId={'zoom-CloudDistanceMinAnalysis-1'}
          zoomedChartId={zoomChartOpen}
          zoomCharts={handleZoomCharts}
          zoomChartsClose={handleZoomChartsClose}
          averageUpperDistance={averageUpperDistance}
          medianUpperDistance={medianUpperDistance}
          averagePlus3SD={averagePlus3SD}
          averageMinus3SD={averageMinus3SD}
          searchType={searchConditions.searchType}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <LFCChartsCloudDistanceMinDistribution
          title={`${t('距離分布')}`}
          source={graphBaseData}
          exportFilename={`CloudDistance_${t('最小値距離分布')}`}
          height={'20vh'}
          zoomChartId={'zoom-CloudDistanceMinAnalysis-2'}
          zoomedChartId={zoomChartOpen}
          zoomCharts={handleZoomCharts}
          zoomChartsClose={handleZoomChartsClose}
          xRenge={xRenge}
          averageUpperDistance={averageUpperDistance}
          medianUpperDistance={medianUpperDistance}
          averagePlus3SD={averagePlus3SD}
          averageMinus3SD={averageMinus3SD}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <LFCChartsCloudDistanceMinVariationTrend
          title={t('ばらつき推移')}
          source={graphBaseData}
          ldata={listData}
          exportFilename={`CloudDistance_${t('最小値ばらつき推移')}`}
          zoomChartId={'zoom-CloudDistanceMinAnalysis-3'}
          zoomedChartId={zoomChartOpen}
          zoomCharts={handleZoomCharts}
          zoomChartsClose={handleZoomChartsClose}
          searchValue={searchConditions}
          height={'20vh'}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TableContainer component={Paper}>
          <Table sx={{minWidth: 600}} size="small">
            <TableHead>
              <TableRow>
                <TableCell align="center" colSpan={2}>
                  {t('規格')}
                </TableCell>
                <TableCell align="center" colSpan={2}>
                  {t('実績')}
                </TableCell>
                <TableCell align="center" colSpan={2}>
                  {t('傾向ばらつき管理')}
                </TableCell>
                <TableCell align="center" colSpan={2}>
                  {t('工程能力')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell align="center">{t('上限')}</TableCell>
                <TableCell align="center">
                  {fmtSizeByMm(
                    listData.filter((a: any) => a.map === searchConditions.map)[0]?.upper_limit
                  )}
                </TableCell>
                <TableCell align="center">{t('最大')}</TableCell>
                <TableCell align="center">{fmtSizeByMm(maxUpperDistance)}</TableCell>
                <TableCell align="center">{t('平均+3σ')}</TableCell>
                <TableCell align="center">{fmtSizeByMm(averagePlus3SD)}</TableCell>
                <TableCell align="center" rowSpan={2}>
                  {t('Cp')}
                </TableCell>
                <TableCell align="center" rowSpan={2}>
                  {fmtFloat(cp)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align="center" rowSpan={2}>
                  {t('目標')}
                </TableCell>
                <TableCell align="center" rowSpan={2}>
                  -
                </TableCell>
                <TableCell align="center">{t('平均')}</TableCell>
                <TableCell align="center">{fmtSizeByMm(averageUpperDistance)}</TableCell>
                <TableCell align="center" rowSpan={2}>
                  {t('標準偏差σ')}
                </TableCell>
                <TableCell align="center" rowSpan={2}>
                  {fmtSizeByMm(standardDeviation)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align="center">{t('中央')}</TableCell>
                <TableCell align="center">{fmtSizeByMm(medianUpperDistance)}</TableCell>
                <TableCell align="center" rowSpan={2}>
                  {t('Cpk')}
                </TableCell>
                <TableCell align="center" rowSpan={2}>
                  {fmtFloat(cpk)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align="center">{t('下限')}</TableCell>
                <TableCell align="center">
                  {fmtSizeByMm(
                    listData.filter((a: any) => a.map === searchConditions.map)[0]?.lower_limit
                  )}
                </TableCell>
                <TableCell align="center">{t('最小')}</TableCell>
                <TableCell align="center">{fmtSizeByMm(minUpperDistance)}</TableCell>
                <TableCell align="center">{t('平均-3σ')}</TableCell>
                <TableCell align="center">{fmtSizeByMm(averageMinus3SD)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <Typography align="center">
          {listData.length > 0 &&
          listData.filter((b: any) => b.map === searchConditions.map)?.[0]?.hasMultipleLimit
            ? '※規格複数のため最新値'
            : ''}
        </Typography>
      </Grid>
    </Grid>
  );
};
